import React, { useState, useRef } from 'react';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfo, maritalStatus, claimExemptionsOptions, additionalExemptions,
} from './data';
import {
  Flex, Text, ActionNote, Box, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function WestVirginiaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // basicInfo question from data.js:
  const basicInfoForm = useRef();
  const basicInfoWithAnswers = getInitialQuestions(basicInfo);
  const [basicInfoQuestions, setBasicInfoQuestions] = useState(basicInfoWithAnswers);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfoQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfoQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfoQuestions(questions);
        }}
        ref={basicInfoForm}
      />
    </Flex>
  );

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);
  const renderIsExemptedFromTax = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired>
        Are you an employee who reside in Kentucky, Maryland, Ohio, Pennsylvania,
        Virginia or a Military Spouse exempt from income tax on wages?
      </S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={isExemptFromWithholding}
              checked={isExemptFromWithholding}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setIsExemptFromWithholding(!isExemptFromWithholding);
              }}
            />
            <Box ml={2} />
            Yes
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={!isExemptFromWithholding}
              checked={!isExemptFromWithholding}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setIsExemptFromWithholding(!isExemptFromWithholding);
              }}
            />
            <Box ml={2} />
            No
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const maritalStatusForm = useRef();
  const maritalStatusQuestionWithAnswers = getInitialQuestions(maritalStatus);
  const [maritalStatusQuestion, setMaritalStatusQuestion] = useState(maritalStatusQuestionWithAnswers);

  const maritalExemptionsForm = useRef();
  const getMaritalStatusExemptions = (questions) => {
    if (questions[0].answer.answer.marital_status !== '' || data.marital_status) {
      const ms = questions[0].answer.answer.marital_status || data.marital_status;
      const maritalStatusExemptionQuestions = claimExemptionsOptions[ms];
      const maritalStatusExemptionAnswers = getInitialQuestions(maritalStatusExemptionQuestions);
      return maritalStatusExemptionAnswers;
    }
  };
  const [maritalExemptionsQuestions, setMaritalExemptionsQuestions] = useState(
    data.marital_status ? getMaritalStatusExemptions(maritalStatusQuestion) : [],
  );

  const renderMaritalExemptions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={maritalExemptionsQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = maritalExemptionsQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setMaritalExemptionsQuestions(questions);
        }}
        ref={maritalExemptionsForm}
      />
    </Flex>
  );

  const additionalExemptionsForm = useRef();
  const additionalExemptionsQuestionsWithAnswers = getInitialQuestions(additionalExemptions);
  const [additionalExemptionsQuestions, setAdditionalExemptionsQuestions] = useState(
    additionalExemptionsQuestionsWithAnswers,
  );

  const [withholdAtLowerRate, setWithholdAtLowerRate] = useState(data.withhold_tax_at_lower_rate || false);

  const renderExemptions = (isReviewStep) => (
    <Flex column>
      <Text style={{ fontSize: '16px', color: '#525252' }}>Exemptions</Text>
      <Flex column mt="20px">
        <FormConsumer
          questions={maritalStatusQuestion}
          {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
          onQuestionChange={({ question, value }) => {
            const questions = maritalStatusQuestion.map((q) => {
              if (q.id !== question.id) return q;
              const answer = {
                ...q.answer,
                answer: {
                  ...q.answer.answer,
                  [q.id]: value,
                },
              };
              const updatedQuestion = { ...q, answer };
              return updatedQuestion;
            });
            setMaritalStatusQuestion(questions);
            setMaritalExemptionsQuestions(getMaritalStatusExemptions(questions));
          }}
          ref={maritalStatusForm}
        />
        {maritalStatusQuestion[0].answer.answer.marital_status !== '' && renderMaritalExemptions(isReviewStep)}
      </Flex>
      <S.QuestionLabel mb="7px">
        Check this box if you are single, head of household, or married and your spouse does not work, and you are
        receiving wages from only one job, and wish to have your tax withheld at a lower rate. (Line 5)
      </S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={withholdAtLowerRate}
              checked={withholdAtLowerRate}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setWithholdAtLowerRate(e.target.checked);
              }}
            />
            <Box ml={2} />
            Yes
          </Flex>
        </label>
      </S.QuestionWrapper>
      <FormConsumer
        questions={additionalExemptionsQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalExemptionsQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalExemptionsQuestions(questions);
        }}
        ref={additionalExemptionsForm}
      />
    </Flex>
  );
  // calculate total exemptions to send to backend
  const calculateTotalExemptions = () => {
    const numExemptionsString = maritalExemptionsQuestions[0].answer.answer.single_claim_exemption
      || maritalExemptionsQuestions[0].answer.answer.married_claim_exemption;
    const lineOneOrTwo = parseInt(numExemptionsString);
    const lineThree = parseInt(additionalExemptionsQuestions[0].answer.answer.exemptions_for_dependents) || 0;
    const lineFour = lineOneOrTwo + lineThree;

    return lineFour;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderIsExemptedFromTax(false)}
          {!isExemptFromWithholding && renderExemptions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (maritalExemptionsForm && maritalExemptionsForm.current) {
              maritalExemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (maritalStatusForm && maritalStatusForm.current) {
              maritalStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (additionalExemptionsForm && additionalExemptionsForm.current) {
              additionalExemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              let maritalStatusData;
              let maritalExemptionData;
              let additionalExemptionsData;
              let totalExemptionsData;

              const basicInfoData = basicInfoQuestions
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              if (isExemptFromWithholding) {
                /* Resetting fields to prevent storing old data */
                maritalStatusData = resetInitialQuestions(maritalStatusQuestion)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalExemptionsData = resetInitialQuestions(additionalExemptionsQuestions)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                maritalExemptionData = { single_claim_exemption: '', married_claim_exemption: '' };
                totalExemptionsData = { total_exemptions: '', withhold_tax_at_lower_rate: '' };
              } else {
                maritalStatusData = maritalStatusQuestion
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                maritalExemptionData = maritalExemptionsQuestions
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalExemptionsData = additionalExemptionsQuestions
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                const totalExemptionsTemp = calculateTotalExemptions() || 0;
                totalExemptionsData = {
                  total_exemptions: totalExemptionsTemp,
                  withhold_tax_at_lower_rate: withholdAtLowerRate,
                };
              }

              const res = {
                ...basicInfoData,
                ...maritalStatusData,
                ...maritalExemptionData,
                ...additionalExemptionsData,
                ...totalExemptionsData,
                is_exempt: isExemptFromWithholding,
              };

              saveFormData(res, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderIsExemptedFromTax(true)}
        {!isExemptFromWithholding && renderExemptions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default WestVirginiaForm;
